import { IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbortIcon, DownloadIcon } from '@/assets/icons';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { useFileTypeValidator } from '@/modules/common/hooks/useFileValidator';
import { convertPascalKeysToCamel } from './helper';
import { UploadFile } from '@mui/icons-material';
import FileUploadStatus from './FileStatus';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { FmsConfig } from '@/modules/simulation/helpers/customTypes';
import { logger } from '@common/logging/logger';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';
import { PROJECT_FILES_ROOT_CONTAINER, FMS_CONFIG_CONTAINER } from '@/modules/api/constant';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';

interface Props {
  disabled?: boolean;
  customFmsConfigUrl?: string;
  onUploadFmsConfig: (url: string, fmsConfig: FmsConfig) => void;
  onRemoveUploadedFmsConfig: () => void;
}

export const DownloadAndUploadFMSConfig = ({
  disabled = false,
  customFmsConfigUrl,
  onUploadFmsConfig,
  onRemoveUploadedFmsConfig,
}: Props) => {
  const { t } = useTranslation('interface');
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const { validateFileType } = useFileTypeValidator();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSnackbar } = useSnackbarStore();

  const fileApi = useMemo(() => FileServiceConnector.getInstance(), []);

  const [fileUploaded, setFileUploaded] = useState<boolean>(!!customFmsConfigUrl);

  const buildFmsConfigFileRef = (fileName: string) => `${FMS_CONFIG_CONTAINER}/${fileName}`;

  const prepareAndDownloadFMSConfig = useCallback(() => {
    simSvc.downloadFmsConfig();
  }, [simSvc]);

  const UploadFmsConfigFileToFileStorage = useCallback(
    async (file: Blob, fileRef: string, extension: 'json') => {
      try {
        const basePath = buildRevisionFileStorageBasePath();
        if (!basePath) throw Error('basePath not defined.');

        await fileApi.addFile(
          `${basePath}/${fileRef}`,
          file,
          extension,
          PROJECT_FILES_ROOT_CONTAINER,
        );
      } catch (error) {
        logger.error('Error uploading fms config to file storage', error);
        return { success: false };
      }

      return { success: true };
    },
    [fileApi],
  );

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fmsConfigFile = event.target.files?.[0];

    if (!fmsConfigFile || !validateFileType(fmsConfigFile, ['json'])) {
      return;
    }

    if (!fmsConfigFile) {
      logger.error('No file selected');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const fileContent = await fmsConfigFile.text();

      try {
        const fmsConfigFileRef = buildFmsConfigFileRef(fmsConfigFile.name);

        const fileUploadResult = await UploadFmsConfigFileToFileStorage(
          new Blob([fileContent], { type: fmsConfigFile.type }),
          fmsConfigFileRef,
          'json',
        );

        if (!fileUploadResult.success) {
          showSnackbar(t('errors:files.upload_failed'));
          return;
        }

        const parsedFile = JSON.parse(fileContent);
        const fmsConfig = convertPascalKeysToCamel(parsedFile) as FmsConfig;
        onUploadFmsConfig(fmsConfigFileRef, fmsConfig);
        setFileUploaded(true);
      } catch (error) {
        logger.error('Error running simulation with FMS config:', error);
      }
    };

    reader.readAsText(fmsConfigFile);
  };

  const uploadFmsConfig = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const removeUploadedFmsConfig = () => {
    onRemoveUploadedFmsConfig();
    setFileUploaded(false);
  };

  const handleActionKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, callback: () => void) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        callback();
      }
    },
    [disabled],
  );

  return (
    <Stack spacing={1} mb={2}>
      <PropertiesTitle
        fontSize={14}
        fontWeight={400}
        value={t('simulation.experimental_settings.title')}
      />
      <Typography variant='body2' sx={{ fontSize: '10px' }}>
        {t('simulation.experimental_settings.description')}
      </Typography>
      <Stack
        justifyContent='center'
        alignItems='center'
        flexDirection='row'
        gap={1}
        sx={{ width: '100%' }}
      >
        <FileUploadStatus
          fileUploaded={fileUploaded}
          fileName={SimulationUtil.getFileNameFromUrl(customFmsConfigUrl || '')}
        />
        <StyledTooltip
          disabled={!customFmsConfigUrl}
          title={t('simulation.experimental_settings.remove_file')}
        >
          <span>
            <IconButton
              sx={{ p: 0, mr: '3px' }}
              disabled={disabled || !customFmsConfigUrl}
              onClick={removeUploadedFmsConfig}
            >
              <AbortIcon />
            </IconButton>
          </span>
        </StyledTooltip>
      </Stack>
      <StyledTooltip
        disabled={disabled}
        title={t('simulation.experimental_settings.download_fms_config_disabled')}
        openOnlyWhenDisabled
        enterDelay={0}
      >
        <span style={{ display: 'block', width: '100%' }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            role='button'
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={disabled ? undefined : prepareAndDownloadFMSConfig}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
              handleActionKeyDown(event, prepareAndDownloadFMSConfig)
            }
            sx={{
              minHeight: 24,
              textAlign: 'left',
              justifyContent: 'flex-start',
              py: 0,
              width: '100%',
              fontSize: '10px',
              color: disabled ? 'text.disabled' : 'text.primary',
              cursor: disabled ? 'default' : 'pointer',
              '&:hover': {
                color: disabled ? 'text.disabled' : 'primary.main',
              },
            }}
          >
            <Typography component='span' sx={{ fontSize: '10px', color: 'inherit' }}>
              {t('simulation.experimental_settings.download_fms_config')}
            </Typography>
            <DownloadIcon color={disabled ? 'disabled' : 'primary'} />
          </Stack>
        </span>
      </StyledTooltip>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        role='button'
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : uploadFmsConfig}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
          handleActionKeyDown(event, uploadFmsConfig)
        }
        sx={{
          minHeight: 24,
          textAlign: 'left',
          width: '100%',
          fontSize: 10,
          color: disabled ? 'text.disabled' : 'text.primary',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': {
            color: disabled ? 'text.disabled' : 'primary.main',
          },
        }}
      >
        <Typography component='span' sx={{ fontSize: '10px', color: 'inherit' }}>
          {t('simulation.experimental_settings.uploadFMSConfig')}
        </Typography>
        <UploadFile color={disabled ? 'disabled' : 'primary'} />
      </Stack>
      <input
        type='file'
        accept='.json'
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelection}
        hidden
      />
    </Stack>
  );
};
